import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';
import { AvcCoreDataBaseUrl } from '@garmin-avcloud/avcloud-fly-web-common/_shared';
import { FLY_WEB_COMMON_CONFIG } from '@garmin-avcloud/avcloud-fly-web-common/config';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams, HttpContext } from '@angular/common/http';

/**
 * Custom HttpParameterCodec
 * Workaround for https://github.com/angular/angular/issues/18261
 */
class CustomHttpParameterCodec {
  encodeKey(k) {
    return encodeURIComponent(k);
  }
  encodeValue(v) {
    return encodeURIComponent(v);
  }
  decodeKey(k) {
    return decodeURIComponent(k);
  }
  decodeValue(v) {
    return decodeURIComponent(v);
  }
}
const BASE_PATH = new InjectionToken('basePath');
const COLLECTION_FORMATS = {
  'csv': ',',
  'tsv': '   ',
  'ssv': ' ',
  'pipes': '|'
};
class AircraftReadConfiguration {
  constructor(configurationParameters = {}) {
    this.apiKeys = configurationParameters.apiKeys;
    this.username = configurationParameters.username;
    this.password = configurationParameters.password;
    this.accessToken = configurationParameters.accessToken;
    this.basePath = configurationParameters.basePath;
    this.withCredentials = configurationParameters.withCredentials;
    this.encoder = configurationParameters.encoder;
    if (configurationParameters.encodeParam) {
      this.encodeParam = configurationParameters.encodeParam;
    } else {
      this.encodeParam = param => this.defaultEncodeParam(param);
    }
    if (configurationParameters.credentials) {
      this.credentials = configurationParameters.credentials;
    } else {
      this.credentials = {};
    }
    // init default bearerAuth credential
    if (!this.credentials['bearerAuth']) {
      this.credentials['bearerAuth'] = () => {
        return typeof this.accessToken === 'function' ? this.accessToken() : this.accessToken;
      };
    }
  }
  /**
   * Select the correct content-type to use for a request.
   * Uses {@link AircraftReadConfiguration#isJsonMime} to determine the correct content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param contentTypes - the array of content types that are available for selection
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderContentType(contentTypes) {
    if (contentTypes.length === 0) {
      return undefined;
    }
    const type = contentTypes.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return contentTypes[0];
    }
    return type;
  }
  /**
   * Select the correct accept content-type to use for a request.
   * Uses {@link AircraftReadConfiguration#isJsonMime} to determine the correct accept content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param accepts - the array of content types that are available for selection.
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderAccept(accepts) {
    if (accepts.length === 0) {
      return undefined;
    }
    const type = accepts.find(x => this.isJsonMime(x));
    if (type === undefined) {
      return accepts[0];
    }
    return type;
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    const jsonMime = new RegExp('^(application\/json|[^;/ \t]+\/[^;/ \t]+[+]json)[ \t]*(;.*)?$', 'i');
    return mime !== null && (jsonMime.test(mime) || mime.toLowerCase() === 'application/json-patch+json');
  }
  lookupCredential(key) {
    const value = this.credentials[key];
    return typeof value === 'function' ? value() : value;
  }
  defaultEncodeParam(param) {
    // This implementation exists as fallback for missing configuration
    // and for backwards compatibility to older typescript-angular generator versions.
    // It only works for the 'simple' parameter style.
    // Date-handling only works for the 'date-time' format.
    // All other styles and Date-formats are probably handled incorrectly.
    //
    // But: if that's all you need (i.e.: the most common use-case): no need for customization!
    const value = param.dataFormat === 'date-time' && param.value instanceof Date ? param.value.toISOString() : param.value;
    return encodeURIComponent(String(value));
  }
}

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AircraftReadOnlyControllerService = /*#__PURE__*/(() => {
  class AircraftReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getAircraft(customerGuid, includeMasterAircraftUuid, includeDeleted, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerGuid !== undefined && customerGuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerGuid, 'customerGuid');
      }
      if (includeMasterAircraftUuid !== undefined && includeMasterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeMasterAircraftUuid, 'includeMasterAircraftUuid');
      }
      if (includeDeleted !== undefined && includeDeleted !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeDeleted, 'includeDeleted');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAircraftByAircraftUuid(aircraftUuid, includeMasterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling getAircraftByAircraftUuid.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (includeMasterAircraftUuid !== undefined && includeMasterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeMasterAircraftUuid, 'includeMasterAircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCustomAircraftImage(aircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling getCustomAircraftImage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/image.png`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchAircraft(aircraftSearchRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftSearchRequest === null || aircraftSearchRequest === undefined) {
        throw new Error('Required parameter aircraftSearchRequest was null or undefined when calling searchAircraft.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (aircraftSearchRequest !== undefined && aircraftSearchRequest !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, aircraftSearchRequest, 'aircraftSearchRequest');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/search`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchAircraftDevices(aircraftDeviceSearchRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftDeviceSearchRequest === null || aircraftDeviceSearchRequest === undefined) {
        throw new Error('Required parameter aircraftDeviceSearchRequest was null or undefined when calling searchAircraftDevices.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/devices/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftDeviceSearchRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchAircraftPost(aircraftSearchRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftSearchRequest === null || aircraftSearchRequest === undefined) {
        throw new Error('Required parameter aircraftSearchRequest was null or undefined when calling searchAircraftPost.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftSearchRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchYarmouthLegacyAircraft(systemIds, searchArchived, observe = 'body', reportProgress = false, options) {
      if (systemIds === null || systemIds === undefined) {
        throw new Error('Required parameter systemIds was null or undefined when calling searchYarmouthLegacyAircraft.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (systemIds) {
        systemIds.forEach(element => {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, element, 'systemIds');
        });
      }
      if (searchArchived !== undefined && searchArchived !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, searchArchived, 'searchArchived');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/search/yarmouth-legacy`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchYarmouthLegacyAircraftPost(yarmouthSearchRequest, observe = 'body', reportProgress = false, options) {
      if (yarmouthSearchRequest === null || yarmouthSearchRequest === undefined) {
        throw new Error('Required parameter yarmouthSearchRequest was null or undefined when calling searchYarmouthLegacyAircraftPost.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/search/yarmouth-legacy`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: yarmouthSearchRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    validateAircraft(aircraftUuid, aircraftValidationRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling validateAircraft.');
      }
      if (aircraftValidationRequest === null || aircraftValidationRequest === undefined) {
        throw new Error('Required parameter aircraftValidationRequest was null or undefined when calling validateAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/validate`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftValidationRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    validatePromotion(aircraftUuid, promoteRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling validatePromotion.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/promotion/validate`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: promoteRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    validateTailNumber(tailNumberRequest, observe = 'body', reportProgress = false, options) {
      if (tailNumberRequest === null || tailNumberRequest === undefined) {
        throw new Error('Required parameter tailNumberRequest was null or undefined when calling validateTailNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/tail-number/validate`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: tailNumberRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    validateTailNumberRegex(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/tail-number/validate/regex`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AircraftReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AircraftReadOnlyControllerService,
        factory: AircraftReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AircraftReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AircraftSyncReadOnlyControllerService = /*#__PURE__*/(() => {
  class AircraftSyncReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getAircraftSync(customerGuid, since, observe = 'body', reportProgress = false, options) {
      if (customerGuid === null || customerGuid === undefined) {
        throw new Error('Required parameter customerGuid was null or undefined when calling getAircraftSync.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerGuid !== undefined && customerGuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerGuid, 'customerGuid');
      }
      if (since !== undefined && since !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, since, 'since');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/sync`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAircraftSyncByAircraftUuid(aircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling getAircraftSyncByAircraftUuid.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft/sync/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AircraftSyncReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftSyncReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AircraftSyncReadOnlyControllerService,
        factory: AircraftSyncReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AircraftSyncReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AircraftSystemReadOnlyControllerService = /*#__PURE__*/(() => {
  class AircraftSystemReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getAircraftSystem(uuid, aircraftUuid, includeMasterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getAircraftSystem.');
      }
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling getAircraftSystem.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (aircraftUuid !== undefined && aircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, aircraftUuid, 'aircraftUuid');
      }
      if (includeMasterAircraftUuid !== undefined && includeMasterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeMasterAircraftUuid, 'includeMasterAircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAircraftSystemSummaries(aircraftUuid, includeMasterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling getAircraftSystemSummaries.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (aircraftUuid !== undefined && aircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, aircraftUuid, 'aircraftUuid');
      }
      if (includeMasterAircraftUuid !== undefined && includeMasterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeMasterAircraftUuid, 'includeMasterAircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/summarize`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAircraftSystemSummary(uuid, aircraftUuid, includeMasterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getAircraftSystemSummary.');
      }
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling getAircraftSystemSummary.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (aircraftUuid !== undefined && aircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, aircraftUuid, 'aircraftUuid');
      }
      if (includeMasterAircraftUuid !== undefined && includeMasterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeMasterAircraftUuid, 'includeMasterAircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/summarize`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getAircraftSystems(aircraftUuid, includeMasterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling getAircraftSystems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (aircraftUuid !== undefined && aircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, aircraftUuid, 'aircraftUuid');
      }
      if (includeMasterAircraftUuid !== undefined && includeMasterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeMasterAircraftUuid, 'includeMasterAircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchAircraftSystems(aircraftSystemSearchRequest, observe = 'body', reportProgress = false, options) {
      if (aircraftSystemSearchRequest === null || aircraftSystemSearchRequest === undefined) {
        throw new Error('Required parameter aircraftSystemSearchRequest was null or undefined when calling searchAircraftSystems.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-systems/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: aircraftSystemSearchRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AircraftSystemReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftSystemReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AircraftSystemReadOnlyControllerService,
        factory: AircraftSystemReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AircraftSystemReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let AircraftValidationReadOnlyControllerService = /*#__PURE__*/(() => {
  class AircraftValidationReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getAircraftTailNumberRegex(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-validation/tail-number/regex`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSystemIdRegex(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-validation/system-id/regex`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    validateAircraftTailNumber(tailNumberRequest, observe = 'body', reportProgress = false, options) {
      if (tailNumberRequest === null || tailNumberRequest === undefined) {
        throw new Error('Required parameter tailNumberRequest was null or undefined when calling validateAircraftTailNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-validation/tail-number`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: tailNumberRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    validateSystemId(systemIdRequest, observe = 'body', reportProgress = false, options) {
      if (systemIdRequest === null || systemIdRequest === undefined) {
        throw new Error('Required parameter systemIdRequest was null or undefined when calling validateSystemId.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-validation/system-id`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: systemIdRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function AircraftValidationReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftValidationReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AircraftValidationReadOnlyControllerService,
        factory: AircraftValidationReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AircraftValidationReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CategoriesReadOnlyControllerService = /*#__PURE__*/(() => {
  class CategoriesReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getCategories(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/categories`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCategory(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getCategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/categories/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CategoriesReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CategoriesReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CategoriesReadOnlyControllerService,
        factory: CategoriesReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CategoriesReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ConnectedCockpitRegistrationReadOnlyControllerService = /*#__PURE__*/(() => {
  class ConnectedCockpitRegistrationReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getConnectedCockpitRegistration(masterAircraftUuid, code, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (masterAircraftUuid !== undefined && masterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, masterAircraftUuid, 'masterAircraftUuid');
      }
      if (code !== undefined && code !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, code, 'code');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/connected-cockpit-registrations`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ConnectedCockpitRegistrationReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ConnectedCockpitRegistrationReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ConnectedCockpitRegistrationReadOnlyControllerService,
        factory: ConnectedCockpitRegistrationReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ConnectedCockpitRegistrationReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CustomerAircraftDeviceReadOnlyControllerService = /*#__PURE__*/(() => {
  class CustomerAircraftDeviceReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getCustomerAircraftDevice(id, includeDeleted, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getCustomerAircraftDevice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (includeDeleted !== undefined && includeDeleted !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, includeDeleted, 'includeDeleted');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft-devices/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchCustomerAircraftDevice(customerAircraftDeviceSearchRequest, observe = 'body', reportProgress = false, options) {
      if (customerAircraftDeviceSearchRequest === null || customerAircraftDeviceSearchRequest === undefined) {
        throw new Error('Required parameter customerAircraftDeviceSearchRequest was null or undefined when calling searchCustomerAircraftDevice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerAircraftDeviceSearchRequest !== undefined && customerAircraftDeviceSearchRequest !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerAircraftDeviceSearchRequest, 'customerAircraftDeviceSearchRequest');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft-devices/search`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchCustomerAircraftDevicePost(customerAircraftDeviceSearchRequest, observe = 'body', reportProgress = false, options) {
      if (customerAircraftDeviceSearchRequest === null || customerAircraftDeviceSearchRequest === undefined) {
        throw new Error('Required parameter customerAircraftDeviceSearchRequest was null or undefined when calling searchCustomerAircraftDevicePost.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft-devices/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: customerAircraftDeviceSearchRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CustomerAircraftDeviceReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CustomerAircraftDeviceReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CustomerAircraftDeviceReadOnlyControllerService,
        factory: CustomerAircraftDeviceReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CustomerAircraftDeviceReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let CustomerAircraftReadOnlyControllerService = /*#__PURE__*/(() => {
  class CustomerAircraftReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getAllCustomerAircraftSummary(customerGuid, organizationUuid, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerGuid !== undefined && customerGuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerGuid, 'customerGuid');
      }
      if (organizationUuid !== undefined && organizationUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, organizationUuid, 'organizationUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft/summarize`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCustAcByCustomerGuidOrOrganizationUuid(customerGuid, organizationUuid, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerGuid !== undefined && customerGuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerGuid, 'customerGuid');
      }
      if (organizationUuid !== undefined && organizationUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, organizationUuid, 'organizationUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCustomerAircraft(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getCustomerAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCustomerAircraftByMasterAircraftUuid(masterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (masterAircraftUuid === null || masterAircraftUuid === undefined) {
        throw new Error('Required parameter masterAircraftUuid was null or undefined when calling getCustomerAircraftByMasterAircraftUuid.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft/by-master-aircraft-uuid/${this.configuration.encodeParam({
        name: "masterAircraftUuid",
        value: masterAircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getCustomerAircraftSummary(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getCustomerAircraftSummary.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/summarize`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchCustomerAircraft(customerAircraftSearch, observe = 'body', reportProgress = false, options) {
      if (customerAircraftSearch === null || customerAircraftSearch === undefined) {
        throw new Error('Required parameter customerAircraftSearch was null or undefined when calling searchCustomerAircraft.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerAircraftSearch !== undefined && customerAircraftSearch !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerAircraftSearch, 'customerAircraftSearch');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft/search`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchCustomerAircraftPost(customerAircraftSearch, observe = 'body', reportProgress = false, options) {
      if (customerAircraftSearch === null || customerAircraftSearch === undefined) {
        throw new Error('Required parameter customerAircraftSearch was null or undefined when calling searchCustomerAircraftPost.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/customer-aircraft/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: customerAircraftSearch,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function CustomerAircraftReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CustomerAircraftReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CustomerAircraftReadOnlyControllerService,
        factory: CustomerAircraftReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CustomerAircraftReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let DataPrivacyReadOnlyControllerService = /*#__PURE__*/(() => {
  class DataPrivacyReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    checkPurgeStatus(customerGuid, observe = 'body', reportProgress = false, options) {
      if (customerGuid === null || customerGuid === undefined) {
        throw new Error('Required parameter customerGuid was null or undefined when calling checkPurgeStatus.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/data-privacy/${this.configuration.encodeParam({
        name: "customerGuid",
        value: customerGuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/purge/status`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getDataPrivacyCustomer(customerGuid, observe = 'body', reportProgress = false, options) {
      if (customerGuid === null || customerGuid === undefined) {
        throw new Error('Required parameter customerGuid was null or undefined when calling getDataPrivacyCustomer.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/data-privacy/${this.configuration.encodeParam({
        name: "customerGuid",
        value: customerGuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/portability`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function DataPrivacyReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DataPrivacyReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DataPrivacyReadOnlyControllerService,
        factory: DataPrivacyReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DataPrivacyReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let DealerAircraftReadOnlyControllerService = /*#__PURE__*/(() => {
  class DealerAircraftReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getDealerAircraftByAircraftUuid(aircraftUuid, observe = 'body', reportProgress = false, options) {
      if (aircraftUuid === null || aircraftUuid === undefined) {
        throw new Error('Required parameter aircraftUuid was null or undefined when calling getDealerAircraftByAircraftUuid.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/dealer/aircraft/${this.configuration.encodeParam({
        name: "aircraftUuid",
        value: aircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchDealerAircraft(dealerAircraftSearchRequest, observe = 'body', reportProgress = false, options) {
      if (dealerAircraftSearchRequest === null || dealerAircraftSearchRequest === undefined) {
        throw new Error('Required parameter dealerAircraftSearchRequest was null or undefined when calling searchDealerAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/dealer/aircraft/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: dealerAircraftSearchRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function DealerAircraftReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DealerAircraftReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DealerAircraftReadOnlyControllerService,
        factory: DealerAircraftReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DealerAircraftReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let DefaultAircraftImageReadOnlyControllerService = /*#__PURE__*/(() => {
  class DefaultAircraftImageReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getDefaultAircraftImage(modelId, observe = 'body', reportProgress = false, options) {
      if (modelId === null || modelId === undefined) {
        throw new Error('Required parameter modelId was null or undefined when calling getDefaultAircraftImage.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/aircraft-images/default/${this.configuration.encodeParam({
        name: "modelId",
        value: modelId,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function DefaultAircraftImageReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || DefaultAircraftImageReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: DefaultAircraftImageReadOnlyControllerService,
        factory: DefaultAircraftImageReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return DefaultAircraftImageReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let EngineTypeReadOnlyControllerService = /*#__PURE__*/(() => {
  class EngineTypeReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getEngineType(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getEngineType.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/engine-types/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getEngineTypes(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/engine-types`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function EngineTypeReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || EngineTypeReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: EngineTypeReadOnlyControllerService,
        factory: EngineTypeReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return EngineTypeReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FirewallAllowListReadOnlyControllerService = /*#__PURE__*/(() => {
  class FirewallAllowListReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getFirewallAllowList(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getFirewallAllowList.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/allow-lists/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFirewallAllowLists(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/allow-lists`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FirewallAllowListReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FirewallAllowListReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FirewallAllowListReadOnlyControllerService,
        factory: FirewallAllowListReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FirewallAllowListReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FirewallFeatureReadOnlyControllerService = /*#__PURE__*/(() => {
  class FirewallFeatureReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getFirewallFeature(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getFirewallFeature.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/features/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFirewallFeatures(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/features`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FirewallFeatureReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FirewallFeatureReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FirewallFeatureReadOnlyControllerService,
        factory: FirewallFeatureReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FirewallFeatureReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FirewallServiceReadOnlyControllerService = /*#__PURE__*/(() => {
  class FirewallServiceReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getFirewallService(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getFirewallService.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/services/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getFirewallServices(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/firewall/services`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FirewallServiceReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FirewallServiceReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FirewallServiceReadOnlyControllerService,
        factory: FirewallServiceReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FirewallServiceReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let FixedDeviceReadOnlyControllerService = /*#__PURE__*/(() => {
  class FixedDeviceReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getFixedDevice(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getFixedDevice.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/fixed-devices/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchFixedDevice(fixedDevice, observe = 'body', reportProgress = false, options) {
      if (fixedDevice === null || fixedDevice === undefined) {
        throw new Error('Required parameter fixedDevice was null or undefined when calling searchFixedDevice.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (fixedDevice !== undefined && fixedDevice !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, fixedDevice, 'fixedDevice');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/fixed-devices/search`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchFixedDevicePost(fixedDevice, observe = 'body', reportProgress = false, options) {
      if (fixedDevice === null || fixedDevice === undefined) {
        throw new Error('Required parameter fixedDevice was null or undefined when calling searchFixedDevicePost.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/fixed-devices/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: fixedDevice,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function FixedDeviceReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || FixedDeviceReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: FixedDeviceReadOnlyControllerService,
        factory: FixedDeviceReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return FixedDeviceReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let LegacyModelReadOnlyControllerService = /*#__PURE__*/(() => {
  class LegacyModelReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getLegacyModel(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getLegacyModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/legacy-models/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getLegacyModels(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/legacy-models`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function LegacyModelReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LegacyModelReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LegacyModelReadOnlyControllerService,
        factory: LegacyModelReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LegacyModelReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MakeReadOnlyControllerService = /*#__PURE__*/(() => {
  class MakeReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getMake(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getMake.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/makes/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMakes(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/makes`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MakeReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MakeReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MakeReadOnlyControllerService,
        factory: MakeReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MakeReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftCustomerRoleReadOnlyControllerService = /*#__PURE__*/(() => {
  class MasterAircraftCustomerRoleReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getAllMasterAircraftCustomerRoles(customerGuid, masterAircraftUuid, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerGuid !== undefined && customerGuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerGuid, 'customerGuid');
      }
      if (masterAircraftUuid !== undefined && masterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, masterAircraftUuid, 'masterAircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft-customer-roles`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMasterAircraftCustomerRole(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getMasterAircraftCustomerRole.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft-customer-roles/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftCustomerRoleReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftCustomerRoleReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftCustomerRoleReadOnlyControllerService,
        factory: MasterAircraftCustomerRoleReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftCustomerRoleReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftInfoReadOnlyControllerService = /*#__PURE__*/(() => {
  class MasterAircraftInfoReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getMasterAircraftInfo(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getMasterAircraftInfo.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/info/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftInfoReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftInfoReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftInfoReadOnlyControllerService,
        factory: MasterAircraftInfoReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftInfoReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftReadOnlyControllerService = /*#__PURE__*/(() => {
  class MasterAircraftReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getMasterAircraft(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getMasterAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMasterAircraftSummary(uuid, onlyConnectedCockpitDevices, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getMasterAircraftSummary.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (onlyConnectedCockpitDevices !== undefined && onlyConnectedCockpitDevices !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, onlyConnectedCockpitDevices, 'onlyConnectedCockpitDevices');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/summarize`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    masterAircraftByModelIds(requestBody, observe = 'body', reportProgress = false, options) {
      if (requestBody === null || requestBody === undefined) {
        throw new Error('Required parameter requestBody was null or undefined when calling masterAircraftByModelIds.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft/by-model-ids`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: requestBody,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchMasterAircraft(masterAircraftSearch, observe = 'body', reportProgress = false, options) {
      if (masterAircraftSearch === null || masterAircraftSearch === undefined) {
        throw new Error('Required parameter masterAircraftSearch was null or undefined when calling searchMasterAircraft.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: masterAircraftSearch,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchMasterAircraftLegacy(systemIdLast8, observe = 'body', reportProgress = false, options) {
      if (systemIdLast8 === null || systemIdLast8 === undefined) {
        throw new Error('Required parameter systemIdLast8 was null or undefined when calling searchMasterAircraftLegacy.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (systemIdLast8 !== undefined && systemIdLast8 !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, systemIdLast8, 'systemIdLast8');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/master-aircraft/search/by-system-id-last-8`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftReadOnlyControllerService,
        factory: MasterAircraftReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftSystemReadOnlyControllerService = /*#__PURE__*/(() => {
  class MasterAircraftSystemReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getMasterAircraftSystem(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getMasterAircraftSystem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/systems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMasterAircraftSystems(systemId, active, systemIdLast8, observe = 'body', reportProgress = false, options) {
      if (systemId === null || systemId === undefined) {
        throw new Error('Required parameter systemId was null or undefined when calling getMasterAircraftSystems.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (systemId !== undefined && systemId !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, systemId, 'systemId');
      }
      if (active !== undefined && active !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, active, 'active');
      }
      if (systemIdLast8 !== undefined && systemIdLast8 !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, systemIdLast8, 'systemIdLast8');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/systems`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftSystemReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftSystemReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftSystemReadOnlyControllerService,
        factory: MasterAircraftSystemReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftSystemReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftTailNumberReadOnlyControllerService = /*#__PURE__*/(() => {
  class MasterAircraftTailNumberReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getTailNumber(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getTailNumber.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/tail-numbers/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getTailNumbers(tailNumber, active, dateTimeQuery, observe = 'body', reportProgress = false, options) {
      if (tailNumber === null || tailNumber === undefined) {
        throw new Error('Required parameter tailNumber was null or undefined when calling getTailNumbers.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (tailNumber !== undefined && tailNumber !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, tailNumber, 'tailNumber');
      }
      if (active !== undefined && active !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, active, 'active');
      }
      if (dateTimeQuery !== undefined && dateTimeQuery !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, dateTimeQuery, 'dateTimeQuery');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/tail-numbers`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftTailNumberReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftTailNumberReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftTailNumberReadOnlyControllerService,
        factory: MasterAircraftTailNumberReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftTailNumberReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let MasterAircraftWakeupSettingsReadOnlyControllerService = /*#__PURE__*/(() => {
  class MasterAircraftWakeupSettingsReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getMasterAircraftWakeupSetting(masterAircraftUuid, observe = 'body', reportProgress = false, options) {
      if (masterAircraftUuid === null || masterAircraftUuid === undefined) {
        throw new Error('Required parameter masterAircraftUuid was null or undefined when calling getMasterAircraftWakeupSetting.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/wakeup-settings/by-master-aircraft-uuid/${this.configuration.encodeParam({
        name: "masterAircraftUuid",
        value: masterAircraftUuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getMasterAircraftWakeupSettings(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getMasterAircraftWakeupSettings.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/wakeup-settings/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function MasterAircraftWakeupSettingsReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MasterAircraftWakeupSettingsReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MasterAircraftWakeupSettingsReadOnlyControllerService,
        factory: MasterAircraftWakeupSettingsReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MasterAircraftWakeupSettingsReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let ModelReadOnlyControllerService = /*#__PURE__*/(() => {
  class ModelReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getModel(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/models/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getModelSummaries(makeId, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (makeId !== undefined && makeId !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, makeId, 'makeId');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/models/summarize`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getModelSummary(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getModelSummary.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/models/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}/summarize`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getModels(makeId, icaoType, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (makeId !== undefined && makeId !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, makeId, 'makeId');
      }
      if (icaoType !== undefined && icaoType !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, icaoType, 'icaoType');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/models`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchModels(criteria, size, page, observe = 'body', reportProgress = false, options) {
      if (criteria === null || criteria === undefined) {
        throw new Error('Required parameter criteria was null or undefined when calling searchModels.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (criteria !== undefined && criteria !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, criteria, 'criteria');
      }
      if (size !== undefined && size !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, size, 'size');
      }
      if (page !== undefined && page !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/models/search`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchModelsByFields(modelSearchRequest, size, page, observe = 'body', reportProgress = false, options) {
      if (modelSearchRequest === null || modelSearchRequest === undefined) {
        throw new Error('Required parameter modelSearchRequest was null or undefined when calling searchModelsByFields.');
      }
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (size !== undefined && size !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, size, 'size');
      }
      if (page !== undefined && page !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, page, 'page');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/models/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: modelSearchRequest,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function ModelReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ModelReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ModelReadOnlyControllerService,
        factory: ModelReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ModelReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let OemAircraftModelReadOnlyControllerService = /*#__PURE__*/(() => {
  class OemAircraftModelReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getOemAircraftModel(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getOemAircraftModel.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oem-aircraft-models/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOemAircraftModels(modelId, oemUuid, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (modelId !== undefined && modelId !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, modelId, 'modelId');
      }
      if (oemUuid !== undefined && oemUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, oemUuid, 'oemUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oem-aircraft-models`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function OemAircraftModelReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OemAircraftModelReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OemAircraftModelReadOnlyControllerService,
        factory: OemAircraftModelReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OemAircraftModelReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let OemReadOnlyControllerService = /*#__PURE__*/(() => {
  class OemReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getOem(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getOem.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oems/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOems(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oems`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function OemReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OemReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OemReadOnlyControllerService,
        factory: OemReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OemReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let OemUserReadOnlyControllerService = /*#__PURE__*/(() => {
  class OemUserReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getOemUser(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getOemUser.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oem-users/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getOemUsers(oemUuid, customerGuid, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (oemUuid !== undefined && oemUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, oemUuid, 'oemUuid');
      }
      if (customerGuid !== undefined && customerGuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerGuid, 'customerGuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/oem-users`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function OemUserReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OemUserReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OemUserReadOnlyControllerService,
        factory: OemUserReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return OemUserReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let RoleInviteReadOnlyControllerService = /*#__PURE__*/(() => {
  class RoleInviteReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getRoleInvite(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getRoleInvite.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/role-invites/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getRoleInviteSummary(uuid, observe = 'body', reportProgress = false, options) {
      if (uuid === null || uuid === undefined) {
        throw new Error('Required parameter uuid was null or undefined when calling getRoleInviteSummary.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/role-invites/${this.configuration.encodeParam({
        name: "uuid",
        value: uuid,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: "uuid"
      })}/summarize`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getRoleInvites(customerGuid, masterAircraftUuid, observe = 'body', reportProgress = false, options) {
      let localVarQueryParameters = new HttpParams({
        encoder: this.encoder
      });
      if (customerGuid !== undefined && customerGuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, customerGuid, 'customerGuid');
      }
      if (masterAircraftUuid !== undefined && masterAircraftUuid !== null) {
        localVarQueryParameters = this.addToHttpParams(localVarQueryParameters, masterAircraftUuid, 'masterAircraftUuid');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/role-invites`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        params: localVarQueryParameters,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    searchRoleInvites(roleInviteSearchRequest, observe = 'body', reportProgress = false, options) {
      if (roleInviteSearchRequest === null || roleInviteSearchRequest === undefined) {
        throw new Error('Required parameter roleInviteSearchRequest was null or undefined when calling searchRoleInvites.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      // to determine the Content-Type header
      const consumes = ['application/json'];
      const httpContentTypeSelected = this.configuration.selectHeaderContentType(consumes);
      if (httpContentTypeSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/role-invites/search`;
      return this.httpClient.request('post', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        body: roleInviteSearchRequest,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function RoleInviteReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RoleInviteReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: RoleInviteReadOnlyControllerService,
        factory: RoleInviteReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return RoleInviteReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let SubcategoryReadOnlyControllerService = /*#__PURE__*/(() => {
  class SubcategoryReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getSubcategories(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/subcategories`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getSubcategory(id, observe = 'body', reportProgress = false, options) {
      if (id === null || id === undefined) {
        throw new Error('Required parameter id was null or undefined when calling getSubcategory.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/subcategories/${this.configuration.encodeParam({
        name: "id",
        value: id,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "number",
        dataFormat: "int32"
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function SubcategoryReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SubcategoryReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SubcategoryReadOnlyControllerService,
        factory: SubcategoryReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SubcategoryReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */
let WakeupTypeReadOnlyControllerService = /*#__PURE__*/(() => {
  class WakeupTypeReadOnlyControllerService {
    constructor(httpClient, basePath, configuration) {
      this.httpClient = httpClient;
      this.basePath = 'https://avcloud-coredata-api-dev.aviation.garmin.com/aircraftManagementReadService';
      this.defaultHeaders = new HttpHeaders();
      this.configuration = new AircraftReadConfiguration();
      if (configuration) {
        this.configuration = configuration;
      }
      if (typeof this.configuration.basePath !== 'string') {
        if (Array.isArray(basePath) && basePath.length > 0) {
          basePath = basePath[0];
        }
        if (typeof basePath !== 'string') {
          basePath = this.basePath;
        }
        this.configuration.basePath = basePath;
      }
      this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }
    // @ts-ignore
    addToHttpParams(httpParams, value, key) {
      if (typeof value === "object" && value instanceof Date === false) {
        httpParams = this.addToHttpParamsRecursive(httpParams, value);
      } else {
        httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
      }
      return httpParams;
    }
    addToHttpParamsRecursive(httpParams, value, key) {
      if (value == null) {
        return httpParams;
      }
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          value.forEach(elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
        } else if (value instanceof Date) {
          if (key != null) {
            httpParams = httpParams.append(key, value.toISOString().substr(0, 10));
          } else {
            throw Error("key may not be null if value is Date");
          }
        } else {
          Object.keys(value).forEach(k => httpParams = this.addToHttpParamsRecursive(httpParams, value[k], key != null ? `${key}.${k}` : k));
        }
      } else if (key != null) {
        httpParams = httpParams.append(key, value);
      } else {
        throw Error("key may not be null if value is not object or array");
      }
      return httpParams;
    }
    getWakeupType(type, observe = 'body', reportProgress = false, options) {
      if (type === null || type === undefined) {
        throw new Error('Required parameter type was null or undefined when calling getWakeupType.');
      }
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/wakeup-types/${this.configuration.encodeParam({
        name: "type",
        value: type,
        in: "path",
        style: "simple",
        explode: false,
        dataType: "string",
        dataFormat: undefined
      })}`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    getWakeupTypes(observe = 'body', reportProgress = false, options) {
      let localVarHeaders = this.defaultHeaders;
      let localVarHttpHeaderAcceptSelected = options && options.httpHeaderAccept;
      const providedHeaders = options?.headers;
      if (providedHeaders != null) {
        if (providedHeaders instanceof HttpHeaders) {
          providedHeaders.keys().forEach(key => localVarHeaders = localVarHeaders.append(key, providedHeaders.get(key)));
        } else {
          Object.entries(providedHeaders).forEach(([key, value]) => localVarHeaders = localVarHeaders.append(key, value));
        }
      }
      if (localVarHttpHeaderAcceptSelected === undefined) {
        // to determine the Accept header
        const httpHeaderAccepts = ['*/*', 'application/json'];
        localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
      }
      if (localVarHttpHeaderAcceptSelected !== undefined) {
        localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
      }
      let localVarHttpContext = options && options.context;
      if (localVarHttpContext === undefined) {
        localVarHttpContext = new HttpContext();
      }
      let responseType_ = 'json';
      if (localVarHttpHeaderAcceptSelected) {
        if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
          responseType_ = 'text';
        } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
          responseType_ = 'json';
        } else {
          responseType_ = 'blob';
        }
      }
      let localVarPath = `/wakeup-types`;
      return this.httpClient.request('get', `${this.configuration.basePath}${localVarPath}`, {
        context: localVarHttpContext,
        responseType: responseType_,
        withCredentials: this.configuration.withCredentials,
        headers: localVarHeaders,
        observe: observe,
        reportProgress: reportProgress
      });
    }
    static {
      this.ɵfac = function WakeupTypeReadOnlyControllerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || WakeupTypeReadOnlyControllerService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(BASE_PATH, 8), i0.ɵɵinject(AircraftReadConfiguration, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: WakeupTypeReadOnlyControllerService,
        factory: WakeupTypeReadOnlyControllerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return WakeupTypeReadOnlyControllerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const APIS = [AircraftReadOnlyControllerService, AircraftSyncReadOnlyControllerService, AircraftSystemReadOnlyControllerService, AircraftValidationReadOnlyControllerService, CategoriesReadOnlyControllerService, ConnectedCockpitRegistrationReadOnlyControllerService, CustomerAircraftDeviceReadOnlyControllerService, CustomerAircraftReadOnlyControllerService, DataPrivacyReadOnlyControllerService, DealerAircraftReadOnlyControllerService, DefaultAircraftImageReadOnlyControllerService, EngineTypeReadOnlyControllerService, FirewallAllowListReadOnlyControllerService, FirewallFeatureReadOnlyControllerService, FirewallServiceReadOnlyControllerService, FixedDeviceReadOnlyControllerService, LegacyModelReadOnlyControllerService, MakeReadOnlyControllerService, MasterAircraftCustomerRoleReadOnlyControllerService, MasterAircraftInfoReadOnlyControllerService, MasterAircraftReadOnlyControllerService, MasterAircraftSystemReadOnlyControllerService, MasterAircraftTailNumberReadOnlyControllerService, MasterAircraftWakeupSettingsReadOnlyControllerService, ModelReadOnlyControllerService, OemAircraftModelReadOnlyControllerService, OemReadOnlyControllerService, OemUserReadOnlyControllerService, RoleInviteReadOnlyControllerService, SubcategoryReadOnlyControllerService, WakeupTypeReadOnlyControllerService];

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

/**
 * AircraftManagementService
 * Project for aircraftManagementService
 *
 * The version of the OpenAPI document: 1.0.0-SNAPSHOT
 * Contact: #AVN-ProdEng-Services-Cloud-CoreData@garmin.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
let AircraftReadApiModule = /*#__PURE__*/(() => {
  class AircraftReadApiModule {
    static forRoot(configurationFactory) {
      return {
        ngModule: AircraftReadApiModule,
        providers: [{
          provide: AircraftReadConfiguration,
          useFactory: configurationFactory
        }]
      };
    }
    constructor(parentModule, http) {
      if (parentModule) {
        throw new Error('AircraftReadApiModule is already loaded. Import in your base AppModule only.');
      }
      if (!http) {
        throw new Error('You need to import the HttpClientModule in your AppModule! \n' + 'See also https://github.com/angular/angular/issues/20575');
      }
    }
    static {
      this.ɵfac = function AircraftReadApiModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftReadApiModule)(i0.ɵɵinject(AircraftReadApiModule, 12), i0.ɵɵinject(i1.HttpClient, 8));
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AircraftReadApiModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return AircraftReadApiModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AircraftReadModule = /*#__PURE__*/(() => {
  class AircraftReadModule {
    static {
      this.ɵfac = function AircraftReadModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AircraftReadModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: AircraftReadModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [{
          ngModule: AircraftReadApiModule,
          providers: [{
            provide: AircraftReadConfiguration,
            deps: [FLY_WEB_COMMON_CONFIG],
            useFactory: config => new AircraftReadConfiguration({
              basePath: `${AvcCoreDataBaseUrl[config.environment]}/aircraftManagementReadService`
            })
          }]
        }]
      });
    }
  }
  return AircraftReadModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { APIS, AircraftReadApiModule, AircraftReadConfiguration, AircraftReadModule, AircraftReadOnlyControllerService, AircraftSyncReadOnlyControllerService, AircraftSystemReadOnlyControllerService, AircraftValidationReadOnlyControllerService, BASE_PATH, COLLECTION_FORMATS, CategoriesReadOnlyControllerService, ConnectedCockpitRegistrationReadOnlyControllerService, CustomerAircraftDeviceReadOnlyControllerService, CustomerAircraftReadOnlyControllerService, DataPrivacyReadOnlyControllerService, DealerAircraftReadOnlyControllerService, DefaultAircraftImageReadOnlyControllerService, EngineTypeReadOnlyControllerService, FirewallAllowListReadOnlyControllerService, FirewallFeatureReadOnlyControllerService, FirewallServiceReadOnlyControllerService, FixedDeviceReadOnlyControllerService, LegacyModelReadOnlyControllerService, MakeReadOnlyControllerService, MasterAircraftCustomerRoleReadOnlyControllerService, MasterAircraftInfoReadOnlyControllerService, MasterAircraftReadOnlyControllerService, MasterAircraftSystemReadOnlyControllerService, MasterAircraftTailNumberReadOnlyControllerService, MasterAircraftWakeupSettingsReadOnlyControllerService, ModelReadOnlyControllerService, OemAircraftModelReadOnlyControllerService, OemReadOnlyControllerService, OemUserReadOnlyControllerService, RoleInviteReadOnlyControllerService, SubcategoryReadOnlyControllerService, WakeupTypeReadOnlyControllerService };
